// PHI: holds a parsed census's member rows in form state and submits them.

import { ChangeEvent, useCallback, useEffect, useState } from "react";

import { COLUMNS, FORM_STATE_FIELDS } from "./constants";
import {
  getDentalHMOCustomColumn,
  getDentalPPOCustomColumn,
  getEligibilityClassCustomColumn,
  getMultOptionsCustomColumn,
  getVisionSingleVisionCustomColumn,
} from "./customColumns";
import formatEcosystem from "./format-ecosystem";
import {
  BASE_COVERAGES,
  COVERAGES,
  COVERED_COVERAGES,
  EXISTING,
  IDLE,
  LOADING,
  NEW,
  NO,
  SALARY_BASED,
  SUCCESS,
  TERM_DISABILITY_COVERAGES,
  YES,
} from "./lib/constants";
import {
  buildStandardizedFile,
  downloadExcelFile,
  getFormInitState,
  getWorkbookFromFile,
  isCoveredCoverage,
} from "./lib/utils";
import processCensusFile, {
  ExcludeColumn,
  Options,
} from "./process-census-file";
import {
  ColumnName,
  Coverage,
  CoverageClass,
  CoverageColumn,
  CustomColumn,
  FormCommonProps,
  FormState,
  ProcessedRow,
  SelectedCoverage,
} from "./types";

const {
  DENTAL,
  VISION,
  STD,
  LTD,
  BASIC_LIFE,
  VOLUNTARY_LIFE,
  VOLUNTARY_AD,
  INDIVIDUAL_DISABILITY,
  STATE_DISABILITY,
  WHOLE_LIFE,
  OTHER_COV,
} = COVERAGES;

type Props = FormCommonProps;

type Return = {
  formState: FormState;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onConfirm: () => Promise<void>;
  updateFormStateValue: <T extends keyof FormState>(
    key: T,
    value: FormState[T],
  ) => void;
  updateFormState: (newState: Partial<FormState>) => void;
  isInvalid: () => boolean;
  onDownloadStandardized: () => Promise<void>;
};

function getUnexpectedProcessingErrorState(
  err: unknown,
): Pick<FormState, "state" | "censusError"> {
  let message = "Unknown error processing the census file";
  if (err instanceof Error) {
    message = err.message;
  }

  return {
    state: IDLE,
    censusError: { status: "on-processed-error", message },
  };
}

function useForm(props?: Props): Return {
  const init = props?.init ?? {};
  const listenOnChange = props?.listenOnChange;
  const [formState, setFormState] = useState<FormState>(() =>
    getFormInitState(init),
  );

  useEffect(() => {
    if (listenOnChange) {
      if (!formState.fileError && !formState.censusFile) {
        listenOnChange(formState);
      }
    }
  }, [formState, listenOnChange]);

  const updateFormStateValue = useCallback(
    <T extends keyof FormState>(key: T, value: FormState[T]) => {
      setFormState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const updateFormState = useCallback((newState: Partial<FormState>) => {
    setFormState((prev) => ({ ...prev, ...newState }));
  }, []);

  function onChange(event: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = event.target;
    if (COVERAGE_NAMES.includes(name)) {
      updateFormStateValue(
        "selectedCoverages",
        toggleCoverage(formState.selectedCoverages, name, event.target.checked),
      );
      return;
    }
    switch (name) {
      case FORM_STATE_FIELDS.ROW:
        if (
          value === "" ||
          (parseInt(value) > 0 && Number.isInteger(parseFloat(value)))
        ) {
          updateFormStateValue(name, value);
        }
        break;
      case FORM_STATE_FIELDS.CLASS_NAMES_DIFFER:
      case FORM_STATE_FIELDS.FULL_TIME_EMPLOYEES:
        updateFormStateValue(name, value);
        break;
    }
  }

  const onConfirm = async (): Promise<void> => {
    if (!formState.censusFile || formState.state === LOADING) return;
    if (formState.fileError) {
      updateFormState({ censusFile: null, fileError: null, processedData: [] });
    } else {
      updateFormStateValue("state", LOADING);

      const options: Options = {
        headerRowIndex: parseInt(formState.row, 10),
        excludedColumns: getExcludedColumns(),
        customColumns: getCustomColumns(formState),
      };
      addExtraOptionsToProcessCensusFileOptions(formState, options);

      try {
        const response = await processCensusFile(formState.censusFile, options);
        if (response.status === "success") {
          // For CensusTab flow: inject eligibility class from coverage columns
          // so formatEcosystem and buildStandardizedFile produce correct output
          if (
            props?.useExperimentalCoverageClasses &&
            options.coverageClasses?.length
          ) {
            await injectEligibilityClassFromCoverageColumns(
              formState.censusFile,
              options.headerRowIndex,
              options.coverageClasses,
              response.data,
            );
          }
          try {
            if (props?.onProcessed) {
              const workbook = await buildStandardizedFile(
                formState.censusFile,
                response.data,
              );
              const blob = await workbook.xlsx.writeBuffer();
              const file = new Blob([blob]);

              await props.onProcessed(formatEcosystem(response.data), file);
            }
            updateFormState({ state: SUCCESS, processedData: response.data });
          } catch (err) {
            updateFormState(getUnexpectedProcessingErrorState(err));
          }
        } else {
          updateFormState({ state: IDLE, censusError: response });
        }
      } catch (err) {
        updateFormState(getUnexpectedProcessingErrorState(err));
      }
    }
  };

  function hasDuplicates(array: string[]): boolean {
    return new Set(array).size !== array.length;
  }

  function isInvalid(): boolean {
    const errors: Record<string, boolean> = {
      rowLengthInvalid: formState.row.length === 0,
      censusInvalid: !formState.censusFile,
      fullTimeEmployeesInvalid: formState.fullTimeEmployees.length === 0,
      selectedCoveragesInvalid: !isSelectedCoveragesValid(
        formState.selectedCoverages,
        formState.censusGroupClasses,
      ),
      censusGroupClassesEmpty: formState.censusGroupClasses.length === 0,
      censusGroupClassesInvalid: formState.censusGroupClasses.some(
        (c) => c.trim() === "",
      ),
      censusGroupClassesHasDuplicates: hasDuplicates(
        formState.censusGroupClasses,
      ),
    };

    if (props?.useExperimentalCoverageClasses) {
      errors.classNamesDifferInvalid = !formState.classNamesDiffer;
      errors.coveragesColumnsInvalid = formState.coveragesColumns.some(
        (c) => !c.columnLetter || c.columnLetter.trim() === "",
      );
    }

    return Object.values(errors).some(Boolean);
  }

  const onDownloadStandardized = async (): Promise<void> => {
    if (formState.censusFile) {
      const workbook = await buildStandardizedFile(
        formState.censusFile,
        formState.processedData,
      );
      const currentDate = new Date();
      const fileName = `Standardized Census - ${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}.${currentDate
        .getDate()
        .toString()
        .padStart(2, "0")}.${currentDate.getFullYear()}.xlsx`;
      await downloadExcelFile(fileName, workbook);
    }
  };

  const getExcludedColumns = (): ExcludeColumn[] => {
    const criteriaCovs: Criteria = getCriterias();
    const isAnyExisting = Object.values(criteriaCovs).some(
      (cov) => cov.isExisting,
    );

    const isDentalExistingAndMultiple = getResult(DENTAL.name, 2, criteriaCovs);
    const isVisionExistingAndMultiple = getResult(VISION.name, 2, criteriaCovs);
    const allColumns: { keyField: ExcludeColumn; validator: boolean }[] = [
      {
        keyField: COLUMNS.OCCUPATION,
        validator: [STD.name, LTD.name, INDIVIDUAL_DISABILITY.name].some(
          (cov) => Boolean(formState.selectedCoverages[cov]),
        ),
      },
      {
        keyField: COLUMNS.ELIGIBILITY_CLASS,
        validator: formState.censusGroupClasses.length > 1,
      },
      {
        keyField: COLUMNS.ANNUAL_SALARY,
        validator:
          [
            STD.name,
            LTD.name,
            INDIVIDUAL_DISABILITY.name,
            STATE_DISABILITY.name,
            WHOLE_LIFE.name,
          ].some((cov) => Boolean(formState.selectedCoverages[cov])) ||
          getResult(BASIC_LIFE.name, 3, criteriaCovs) ||
          getResult(VOLUNTARY_LIFE.name, 3, criteriaCovs) ||
          getResult(VOLUNTARY_AD.name, 3, criteriaCovs),
      },
      {
        keyField: COLUMNS.EMPLOYMENT_TYPE,
        validator: formState.fullTimeEmployees === NO,
      },
      {
        keyField: "dental plan coverage",
        validator: getResult(DENTAL.name, 1, criteriaCovs),
      },
      {
        keyField: "dental plan name",
        validator: isDentalExistingAndMultiple,
      },
      {
        keyField: "vision plan coverage",
        validator: getResult(VISION.name, 1, criteriaCovs),
      },
      {
        keyField: "vision plan name",
        validator: isVisionExistingAndMultiple,
      },
      {
        keyField: "std election",
        validator: getResult(STD.name, 1, criteriaCovs),
      },
      {
        keyField: "ltd election",
        validator: getResult(LTD.name, 1, criteriaCovs),
      },
      {
        keyField: "basic life/ad&d ee coverage",
        validator: getResult(BASIC_LIFE.name, 1, criteriaCovs),
      },
      {
        keyField: "vol life ee coverage",
        validator: getResult(VOLUNTARY_LIFE.name, 1, criteriaCovs),
      },
      {
        keyField: "vol ad&d ee coverage",
        validator: getResult(VOLUNTARY_AD.name, 1, criteriaCovs),
      },
      {
        keyField: "benefit",
        validator: isAnyExisting,
      },
      {
        keyField: "coverage tier",
        validator: isAnyExisting,
      },
    ];

    return allColumns.reduce<(typeof allColumns)[0]["keyField"][]>(
      (acc, item) => {
        if (!item.validator) {
          acc.push(item.keyField);
        }
        return acc;
      },
      [],
    );
  };

  const getCriterias = () =>
    Object.values(COVERAGES).reduce((acc, item) => {
      const { hasCoverage, isExisting, isSecondOpt, isThirdOpt } =
        getConditions(item.name);
      return hasCoverage
        ? {
            ...acc,
            [item.name]: {
              isExisting,
              isSecondOpt,
              isThirdOpt,
            },
          }
        : acc;
    }, {});

  const getConditions = (cov: string) => {
    const foundCoverage = formState.selectedCoverages[cov];
    if (!foundCoverage) return { hasCoverage: false };
    const isExisting = foundCoverage.existing === EXISTING;
    const isSecondOpt =
      foundCoverage.dualOption === YES ||
      foundCoverage.fullCoveredClasses === YES ||
      foundCoverage.coverageOption?.value === "tripleOption" ||
      foundCoverage.coverageOption?.value === "dualOption" ||
      foundCoverage.coverageOption?.value === "hmoPpo";
    const isThirdOpt = foundCoverage.benefitStructure === SALARY_BASED;
    return {
      hasCoverage: true,
      isExisting,
      isSecondOpt,
      isThirdOpt,
    };
  };

  return {
    formState,
    onChange,
    onConfirm,
    updateFormStateValue,
    updateFormState,
    isInvalid,
    onDownloadStandardized,
  };
}

type Criteria = {
  [key: string]: {
    isExisting: boolean;
    isSecondOpt: boolean;
    isThirdOpt: boolean;
  };
};

const getResult = (
  coverageName: string,
  typeValidator: number,
  criterias: Criteria,
): boolean => {
  switch (typeValidator) {
    case 1:
      return Boolean(criterias[coverageName]?.isExisting);
    case 2:
      return (
        Boolean(criterias[coverageName]?.isExisting) &&
        Boolean(criterias[coverageName]?.isSecondOpt)
      );
    case 3:
      return Boolean(criterias[coverageName]?.isThirdOpt);
    default:
      return false;
  }
};

function isSelectedCoveragesValid(
  selectedCoverages: SelectedCoverage,
  censusGroupClasses: string[],
): boolean {
  const coverages = Object.values(selectedCoverages);
  if (coverages.length === 0) {
    return false;
  }

  return coverages.every((c) => {
    if (c.existing === NEW) {
      if (isCoveredCoverage(c)) {
        return c.benefitStructure;
      }
      return true;
    } else if (c.existing === EXISTING) {
      // keep covered condition at the top because basic life / ad&d belongs to both
      // covered and term disability
      if (isCoveredCoverage(c)) {
        return c.benefitStructure;
      } else if (TERM_DISABILITY_COVERAGES.includes(c.coverage)) {
        if (!c.fullCoveredClasses) return false;
        if (c.fullCoveredClasses === NO) {
          return true;
        }
        if (censusGroupClasses.length > 1) {
          return c.coverClasses && c.coverClasses.length > 0;
        }
        return true;
      }
      return Boolean(c.dualOption) || isCoverageDentalOrVisionValid(c);
    }
    return BASE_COVERAGES.includes(c.coverage);
  });
}

const FIELDS_BY_OPTION_VALUE: Record<string, string[]> = {
  dualOption: ["low", "high"],
  hmoPpo: ["low", "high"],
  tripleOption: ["low", "medium", "high"],
};

function isCoverageDentalOrVisionValid(c: Coverage): boolean {
  if ([DENTAL.name, VISION.name].includes(c.coverage)) {
    const option = c.coverageOption;
    if (!option) {
      return false;
    }

    const fields = FIELDS_BY_OPTION_VALUE[option.value] ?? [];
    if (fields.length === 0) {
      return true;
    }

    const optionPlans = option.plans || {};
    return fields.every((field) => Boolean(optionPlans[field]));
  }
  return true;
}

function isCoverageCovered(coverage?: Coverage): boolean {
  if (!coverage) {
    return false;
  }

  return coverage.existing === EXISTING && coverage.fullCoveredClasses === YES;
}

const COVERAGE_NAMES: string[] = [
  DENTAL.name,
  VISION.name,
  STD.name,
  LTD.name,
  BASIC_LIFE.name,
  VOLUNTARY_LIFE.name,
  VOLUNTARY_AD.name,
  INDIVIDUAL_DISABILITY.name,
  STATE_DISABILITY.name,
  WHOLE_LIFE.name,
  OTHER_COV.name,
];

const FULL_COVERED_COLUMNS: { coverageName: string; column: ColumnName }[] = [
  { coverageName: LTD.name, column: "ltd election" },
  { coverageName: STD.name, column: "std election" },
  { coverageName: BASIC_LIFE.name, column: "basic life/ad&d ee coverage" },
];

type ExistingCoverageColumns = {
  columns: CustomColumn[];
  isExistingMultiple: boolean;
};

function getNewCoverage(name: string): Coverage {
  if (COVERED_COVERAGES.includes(name)) {
    return { coverage: name, existing: "" };
  }
  if (TERM_DISABILITY_COVERAGES.includes(name)) {
    return { coverage: name, fullCoveredClasses: "", existing: "" };
  }
  return { coverage: name, dualOption: "", existing: "" };
}

function toggleCoverage(
  selectedCoverages: SelectedCoverage,
  name: string,
  checked: boolean,
): SelectedCoverage {
  const newSelectedCoverages = { ...selectedCoverages };
  if (checked) {
    newSelectedCoverages[name] = getNewCoverage(name);
  } else {
    delete newSelectedCoverages[name];
  }
  return newSelectedCoverages;
}

function getEmploymentTypeColumns(fullTimeEmployees: string): CustomColumn[] {
  if (fullTimeEmployees !== YES) {
    return [];
  }
  return [
    {
      column: "employment type",
      skipHeaderValidation: true,
      getCustomValue: () => ({ text: "full-time employees" }),
    },
  ];
}

function getDentalCustomColumns(coverage?: Coverage): ExistingCoverageColumns {
  const option = coverage?.coverageOption;
  if (coverage?.existing !== EXISTING || !option) {
    return { columns: [], isExistingMultiple: false };
  }

  switch (option.value) {
    case "ppo":
      return {
        columns: [getDentalPPOCustomColumn()],
        isExistingMultiple: false,
      };
    case "hmo":
      return {
        columns: [getDentalHMOCustomColumn()],
        isExistingMultiple: false,
      };
    case "tripleOption":
    case "dualOption":
    case "hmoPpo": {
      const labels =
        option.value === "hmoPpo"
          ? { low: "HMO", high: "Single PPO" }
          : { low: "PPO Low", high: "PPO High" };
      const mediumOption =
        option.value === "tripleOption"
          ? { [option.plans.medium.toLocaleLowerCase()]: "PPO Medium" }
          : {};
      return {
        columns: [
          getMultOptionsCustomColumn(COLUMNS.DENTAL_PLAN_NAME, {
            ...mediumOption,
            [option.plans.low.toLocaleLowerCase()]: labels.low,
            [option.plans.high.toLocaleLowerCase()]: labels.high,
          }),
        ],
        isExistingMultiple: true,
      };
    }
    default:
      return { columns: [], isExistingMultiple: false };
  }
}

function getVisionCustomColumns(coverage?: Coverage): ExistingCoverageColumns {
  const option = coverage?.coverageOption;
  if (coverage?.existing !== EXISTING || !option) {
    return { columns: [], isExistingMultiple: false };
  }

  switch (option.value) {
    case "singleVision":
      return {
        columns: [getVisionSingleVisionCustomColumn()],
        isExistingMultiple: false,
      };
    case "tripleOption":
    case "dualOption": {
      const mediumOption =
        option.value === "tripleOption"
          ? { [option.plans.medium.toLocaleLowerCase()]: "Medium" }
          : {};
      return {
        columns: [
          getMultOptionsCustomColumn(COLUMNS.VISION_PLAN_NAME, {
            ...mediumOption,
            [option.plans.low.toLocaleLowerCase()]: "Low",
            [option.plans.high.toLocaleLowerCase()]: "High",
          }),
        ],
        isExistingMultiple: true,
      };
    }
    default:
      return { columns: [], isExistingMultiple: false };
  }
}

function getFullCoveredValue(
  coverClasses: string[],
  classes: string[],
): NonNullable<CustomColumn["getCustomValue"]> {
  return (val, row) => {
    const eligibilityClass =
      row["eligibility class"]?.original?.toLocaleLowerCase() ?? "";
    if (coverClasses.includes(eligibilityClass)) {
      return { text: "Covered" };
    }
    if (classes.includes(eligibilityClass) && val) {
      return val;
    }
    return { text: "Waived" };
  };
}

function getFullCoveredColumns(formState: FormState): CustomColumn[] {
  const { censusGroupClasses, selectedCoverages } = formState;
  const isOneGroupClass = censusGroupClasses.length === 1;
  const classes = censusGroupClasses.map((c) => c.toLocaleLowerCase());

  return FULL_COVERED_COLUMNS.filter(({ coverageName }) =>
    isCoverageCovered(selectedCoverages[coverageName]),
  ).map(({ coverageName, column }) => {
    const coverClasses = (
      selectedCoverages[coverageName]?.coverClasses ?? []
    ).map((c) => c.toLocaleLowerCase());
    return {
      column,
      skipHeaderValidation: true,
      getCustomValue: isOneGroupClass
        ? () => ({ text: "Covered" })
        : getFullCoveredValue(coverClasses, classes),
    };
  });
}

function getClassCustomColumns(formState: FormState): CustomColumn[] {
  if (formState.censusGroupClasses.length === 0) {
    return [];
  }
  const eligibilityClassColumns =
    formState.censusGroupClasses.length === 1
      ? [getEligibilityClassCustomColumn()]
      : [];
  return [...eligibilityClassColumns, ...getFullCoveredColumns(formState)];
}

function getCustomColumns(formState: FormState): CustomColumn[] {
  const dental = getDentalCustomColumns(
    formState.selectedCoverages[DENTAL.name],
  );
  const vision = getVisionCustomColumns(
    formState.selectedCoverages[VISION.name],
  );

  return [
    ...getEmploymentTypeColumns(formState.fullTimeEmployees),
    ...dental.columns,
    ...vision.columns,
    // this column is only available when format is by row but it is ok to always
    // add since it will be ignored if the format is by column
    {
      column: "plan name",
      skipHeaderValidation: !(
        dental.isExistingMultiple || vision.isExistingMultiple
      ),
      getCustomValue: (val) => val ?? { text: "" },
    },
    ...getClassCustomColumns(formState),
  ];
}

function getExistingPlanNames(
  coverage: Coverage | undefined,
  multiOptionValues: string[],
): string[] | undefined {
  if (coverage?.existing !== EXISTING) {
    return undefined;
  }
  const option = coverage.coverageOption;
  if (!option || !multiOptionValues.includes(option.value)) {
    return [];
  }
  const { medium, low, high } = option.plans;
  const planNames =
    option.value === "tripleOption" ? [medium, low, high] : [low, high];
  return planNames.filter(Boolean);
}

function getCoverageClasses(
  coveragesColumns: CoverageColumn[],
): CoverageClass[] {
  return coveragesColumns.flatMap((coverageColumn) => {
    const classNames = coverageColumn.classes.map((c) => c.newName ?? c.name);
    if (classNames.length === 0) {
      return [];
    }
    return [
      {
        coverage: coverageColumn.coverageType,
        column: coverageColumn.columnLetter,
        classes: classNames,
        identifiers: coverageColumn.classes.map((c) => c.identifier),
      },
    ];
  });
}

const addExtraOptionsToProcessCensusFileOptions = (
  formState: FormState,
  options: Options,
): void => {
  if (formState.censusGroupClasses.length > 1) {
    options.classes = formState.censusGroupClasses;
  }
  const dental = getExistingPlanNames(
    formState.selectedCoverages[DENTAL.name],
    ["tripleOption", "dualOption", "hmoPpo"],
  );
  if (dental) {
    options.dental = dental;
  }
  const vision = getExistingPlanNames(
    formState.selectedCoverages[VISION.name],
    ["tripleOption", "dualOption"],
  );
  if (vision) {
    options.vision = vision;
  }
  const coverageClasses = getCoverageClasses(formState.coveragesColumns ?? []);
  if (coverageClasses.length > 0) {
    options.coverageClasses = coverageClasses;
  }
};

/**
 * Injects "eligibility class" into processed rows for the CensusTab flow.
 *
 * In the coverageClasses mechanism, process-census-file stores class data under
 * address keys (e.g. row["I1"]) then deletes them after validation. This leaves
 * row["eligibility class"] empty, which formatEcosystem needs for benefitClassName.
 *
 * This function re-reads the file to recover the class values and injects them
 * into the processed data so both formatEcosystem and buildStandardizedFile
 * produce correct output.
 */
async function injectEligibilityClassFromCoverageColumns(
  file: File,
  headerRowIndex: number,
  coverageClasses: CoverageClass[],
  processedData: ProcessedRow[],
): Promise<void> {
  if (coverageClasses.length === 0 || processedData.length === 0) return;

  // Collect all unique classes across all coverages
  const allClasses = [...new Set(coverageClasses.flatMap((cc) => cc.classes))];
  if (allClasses.length === 0) return;

  // Build lookup: lowercase class name → stable identifier (e.g. "class-1")
  // Uses identifiers so census rows carry backend-generated keys, not positional "Class N" labels.
  // Falls back to "Class N" using global allClasses ordering (matches validateEligibilityClass).
  const globalFallbackIndex = new Map(
    allClasses.map((name, i) => [name.toLocaleLowerCase(), `Class ${i + 1}`]),
  );
  const classLookup = new Map<string, string>();
  for (const cc of coverageClasses) {
    cc.classes.forEach((className, index) => {
      const value =
        cc.identifiers?.[index] ??
        globalFallbackIndex.get(className.toLocaleLowerCase()) ??
        `Class ${index + 1}`;
      classLookup.set(className.toLocaleLowerCase(), value);
    });
  }

  if (allClasses.length === 1) {
    // Single class: set "Class 1" for all employees without re-reading the file
    const identifier =
      coverageClasses[0].identifiers?.[0] ??
      globalFallbackIndex.get(allClasses[0].toLocaleLowerCase()) ??
      "Class 1";
    for (const row of processedData) {
      row["eligibility class"] = {
        status: "valid",
        original: allClasses[0],
        processed: identifier,
      };
    }
    return;
  }

  // Multi-class: re-read the file to get per-row class values
  const columnLetter = coverageClasses[0].column;
  if (!columnLetter) return;

  const workbook = await getWorkbookFromFile(file);
  const worksheet = workbook.worksheets[0];

  // Build map: row number → raw class value from the file
  const classValuesByRow = new Map<number, string>();
  worksheet.eachRow((_excelRow, rowNumber) => {
    if (rowNumber <= headerRowIndex) return;
    const cell = worksheet.getCell(`${columnLetter}${rowNumber}`);
    const classValue = cell?.text?.trim();
    if (classValue) {
      classValuesByRow.set(rowNumber, classValue);
    }
  });

  // Inject class values into processed data
  for (const processedRow of processedData) {
    // Find the file row number from any cell's cellAddress (e.g. "A2" → 2)
    const anyCellWithAddress = Object.values(processedRow).find(
      (cell): cell is NonNullable<typeof cell> & { cellAddress: string } =>
        typeof cell?.cellAddress === "string",
    );
    if (!anyCellWithAddress) continue;

    const rowNumber = parseInt(
      anyCellWithAddress.cellAddress.replace(/[A-Z]+/i, ""),
      10,
    );
    const rawClassValue = classValuesByRow.get(rowNumber);

    if (rawClassValue) {
      const processedValue =
        classLookup.get(rawClassValue.toLocaleLowerCase()) ?? rawClassValue;
      processedRow["eligibility class"] = {
        status: "valid",
        original: rawClassValue,
        processed: processedValue,
      };
    }
  }
}

export {
  getCoverageClasses,
  getCustomColumns,
  getExistingPlanNames,
  toggleCoverage,
};
export default useForm;
